<template>
  <div class="accounts-view">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Comptes</h1>
        <p class="page-subtitle">Gérer les comptes entreprises</p>
      </div>
      <v-btn color="secondary" class="add-btn" @click="dialog = true">
        <v-icon left>mdi-plus</v-icon>
        Nouveau compte
      </v-btn>
    </div>

    <!-- Dialog -->
    <v-dialog v-model="dialog" scrollable max-width="650px" class="modern-dialog">
      <v-card class="modern-card">
        <v-card-title class="dialog-title">
          <v-icon left color="primary">mdi-domain</v-icon>
          {{ form_title }}
        </v-card-title>
        <v-card-text class="dialog-content">
          <v-row>
            <v-col cols="12">
              <v-text-field
                label="Nom Entreprise"
                v-model="account.name"
                outlined
                dense
                prepend-inner-icon="mdi-office-building"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Téléphone"
                v-model="account.phone"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="E-mail"
                v-model="account.email"
                outlined
                dense
                prepend-inner-icon="mdi-email"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                label="Adresse"
                v-model="account.address"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
              ></v-text-field>
            </v-col>
            
            <v-col cols="12">
              <v-divider class="my-2"></v-divider>
              <p class="section-label">Informations du contact</p>
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                label="Nom du Contact"
                v-model="account.contact_full_name"
                outlined
                dense
                prepend-inner-icon="mdi-account"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Téléphone Contact"
                v-model="account.contact_phone"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="E-mail du Contact"
                v-model="account.contact_email"
                outlined
                dense
                prepend-inner-icon="mdi-email"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Numéro d'Identification"
                v-model="account.contact_id"
                outlined
                dense
                prepend-inner-icon="mdi-card-account-details"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                :items="id_types"
                item-value="name"
                item-text="name"
                label="Type d'Identification"
                v-model="account.contact_id_type"
                outlined
                dense
                prepend-inner-icon="mdi-card-bulleted"
              ></v-select>
            </v-col>
            
            <v-col cols="12">
              <v-divider class="my-2"></v-divider>
              <p class="section-label">Configuration</p>
            </v-col>
            
            <v-col cols="12" sm="6">
              <v-text-field
                label="Prefix (Sous-domaine)"
                v-model="account.prefix"
                outlined
                dense
                prepend-inner-icon="mdi-web"
                hint="Lettres minuscules et chiffres uniquement"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                :items="rates"
                item-value="id"
                item-text="name"
                label="Devise de Vente"
                v-model="account.rate_id"
                outlined
                dense
                prepend-inner-icon="mdi-currency-usd"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close" class="cancel-btn">
            Annuler
          </v-btn>
          <v-btn
            color="secondary"
            @click="save"
            :loading="loading"
            :disabled="loading"
            class="save-btn"
          >
            <v-icon left small>mdi-check</v-icon>
            Enregistrer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preloader -->
    <PreloaderComponent :enable="enable"></PreloaderComponent>

    <!-- Content -->
    <div class="content-container" v-if="enable">
      <v-card class="data-card">
        <v-card-title class="card-header">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Rechercher..."
            single-line
            hide-details
            outlined
            dense
            class="table-search"
          ></v-text-field>
          <v-spacer></v-spacer>
          <v-chip class="results-chip" small>
            {{ rows.length }} comptes
          </v-chip>
          <v-btn small outlined color="primary" class="ml-2" :loading="downloadingAll" :disabled="logosCount === 0" @click="downloadAllLogos">
            <v-icon left small>mdi-folder-download</v-icon>
            Télécharger tous les logos ({{ logosCount }})
          </v-btn>
        </v-card-title>

        <div v-if="loading" class="text-caption text--secondary mb-3">
          Chargement des comptes… {{ loadedCount }} chargé(s)
        </div>
        <v-alert v-if="loadError && !loading" dense text type="error" class="mb-2">
          Erreur lors du chargement des comptes. Les comptes déjà chargés restent affichés.
          <a @click.prevent="loadAll()" style="text-decoration: underline; cursor: pointer;">Réessayer</a>
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="rows"
          :items-per-page="10"
          :search="search"
          class="modern-table"
        >
          <template v-slot:item.id="{ item }">
            <span class="id-badge">#{{ getAccountNumber(item.id) }}</span>
          </template>
          <template v-slot:item.name="{ item }">
            <div class="account-name">
              <v-avatar size="32" color="primary" class="mr-2">
                <v-img v-if="item.logo_url" :src="item.logo_url" :alt="item.name" />
                <span v-else class="white--text text-caption">{{ item.name.substring(0, 2).toUpperCase() }}</span>
              </v-avatar>
              {{ item.name }}
            </div>
          </template>
          <template v-slot:item.uid="{ item }">
            <span class="uid-prefix" :title="item.uid || ''">{{ businessUidPrefix(item) }}</span>
          </template>
          <template v-slot:item.prefix="{ item }">
            <a :href="'https://' + item.prefix + '.keslakay.com'" target="_blank" class="prefix-link">
              <v-icon small class="mr-1">mdi-open-in-new</v-icon>
              {{ item.prefix }}
            </a>
          </template>
          <template v-slot:item.created_at="{ item }">
            <span>{{ formatDateTime(item.created_at) }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small class="action-btn action-btn-edit" @click="edit(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small class="action-btn action-btn-view" @click="view(item)">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn icon small :disabled="!item.logo_url" @click.stop="downloadLogo(item)" title="Télécharger le logo">
              <v-icon small>mdi-download</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import PreloaderComponent from '../components/PreloaderComponent.vue'
import { logoFileName, saveBlob } from '../utils/logoDownload'

var PAGE_SIZE = 200

export default {
  name: 'AccountsView',
  computed: {
    logosCount() {
      return this.rows.filter(function (r) { return !!r.logo_url }).length
    },
  },
  methods: {
    async downloadLogo(item) {
      try {
        var r = await this.$axios.get('/businesses/' + item.id + '/logo/download', { responseType: 'blob' })
        saveBlob(r.data, logoFileName(item))
      } catch (e) {
        alert('Téléchargement impossible. Réessayez ou vérifiez votre session.')
      }
    },

    async downloadAllLogos() {
      this.downloadingAll = true
      try {
        var r = await this.$axios.get('/businesses/logos/download-all', { responseType: 'blob' })
        var today = new Date().toISOString().slice(0, 10)
        saveBlob(r.data, 'logos_clients_' + today + '.zip')
      } catch (e) {
        alert('Téléchargement de l\'archive impossible.')
      } finally {
        this.downloadingAll = false
      }
    },

    async loadAll() {
      var gen = ++this.generation
      this.rows = []
      this.loadedCount = 0
      this.loading = true
      this.loadError = false
      var skip = 0
      var seenIds = new Set()
      try {
        for (;;) {
          var r = await this.$axios.get('/businesses?orderasc=name&skip=' + skip + '&limit=' + PAGE_SIZE)
          if (gen !== this.generation) return
          var raw = r.data || []
          if (!Array.isArray(raw)) { this.loadError = true; break }
          if (raw.length > PAGE_SIZE || skip > 100000) { this.loadError = true; break }
          var visible = raw
          // Si l'utilisateur est un agent (role_id === 2), filtrer par seller_id
          var user = this.$store.getters.StateUser
          if (user && user.role_id === 2) {
            visible = raw.filter(function(business) {
              return business.seller_id === user.id
            })
          }
          // orderasc=name n'est pas un tri stable côté Postgres : deux comptes
          // homonymes peuvent changer d'ordre entre deux pages et provoquer un
          // doublon/oubli. On dé-duplique défensivement par id.
          visible = visible.filter(function(business) {
            if (!business || seenIds.has(business.id)) return false
            seenIds.add(business.id)
            return true
          })
          this.rows = this.rows.concat(visible)
          this.loadedCount = this.rows.length
          // Afficher le tableau dès la première page reçue
          this.enable = true
          // La fin de pagination se juge sur la page brute, jamais sur la page filtrée
          if (raw.length < PAGE_SIZE) break
          skip += PAGE_SIZE
        }
      } catch (e) {
        if (gen !== this.generation) return
        this.loadError = true
      }
      this.loading = false
      this.enable = true
    },
    validateData: function(){

      if(!/^[a-z0-9]*$/.test(this.account.prefix)){
        alert("Le préfix ne peut contenir que des chiffres et des lettres minuscules")
        return false
      }
      if(this.account.name == ""){
        alert("Vous devez un nom pour le compte")
        return false  
      }

      if(this.account.address == ""){
        alert("Vous devez indiquer une adresse")
        return false
      }

      if(this.account.prefix == ""){
        alert("Vous devez choisir un prefix. Seuls les lettres et les chiffres sont autorisés.")
        return false
      }

      if(this.account.phone == ""){
        alert("Vous devez choisir un téléphone")
        return false
      }

      if(this.account.email == ""){
        alert("Vous devez choisir un email")
        return false
      }

      if(this.account.contact_full_name == ""){
        alert("Vous devez indiquer un nom pour la personne de contact")
        return false
      }

      if(this.account.contact_phone == ""){
        alert("Vous devez indiquer un téléphone pour la personne de contact")
        return false
      }

      if(this.account.contact_email == ""){
        alert("Vous devez indiquer un email pour la personne de contact")
        return false
      }

      if(this.account.contact_id == ""){
        alert("Vous devez indiquer un numéro d'identité pour la personne de contact")
        return false
      }
      return true;
    },
    getAccountNumber: function(id){
        var renewal_number = 1000 + parseInt(id)
        return renewal_number
      },
      save: function(){
        var self = this;
        
        var url = ''
        if(this.validateData()){
          this.loading = true
          if (this.index > -1) { 
            url = '/businesses/'+this.account.id
            this.$axios.put(url+"?data="+encodeURIComponent(JSON.stringify(self.account)))
            .then(response => {
              Object.assign(self.rows[self.index], response.data)
              self.dialog = false
              self.loading = false
              self.reset()
            })
          }else{
            url = '/businesses'
            this.$axios.post(url, self.account)
            .then(response => {
              self.rows.push(response.data.business);
              self.dialog = false
              self.loading = false
              self.reset()
            })
          }
        }
        
      },
      edit: function(item){
        this.index = this.rows.indexOf(item)
        this.account = Object.assign({}, item)
        this.form_title = "Editer Compte : "+this.account.name
        this.dialog = true
      },
      close: function(){
        this.dialog = false
        this.reset()
        this.loading = false
      },
      view: function(item){
        this.$router.push('/accounts/'+item.id)
      },
      businessUidPrefix: function(item) {
        if (!item || item.uid == null || item.uid === '') return '—'
        var s = String(item.uid)
        return s.length <= 8 ? s : s.substring(0, 8)
      },
      formatDateTime: function(date) {
        if (!date) return '--'
        var d = new Date(date)
        if (isNaN(d.getTime())) return '--'
        return d.toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      },
      reset: function(){
        this.account = {
          name: '',
          rate_id:1, 
          prefix:'',
          address: '', 
          phone: '', 
          email: '', 
          contact_full_name: '', 
          contact_phone: '', 
          contact_email: '',
          contact_id:'',
          contact_id_type:"CIN"
        }
        this.index = -1
        this.form_title = 'Nouveau Compte'
      }
  },
  mounted(){
    this.loadAll()
  },
  beforeDestroy() {
    this.generation++
  },
  data: () => ({
      search: '',
      index: -1,
      loading: false,
      form_title: "Nouveau Compte",
      generation: 0,
      loadError: false,
      loadedCount: 0,
      downloadingAll: false,
      rates: [{name:'HTG', id:1}, {name:'USD', id:2}],
      id_types: [{name:'CIN'}, {name:'NIF'}, {name:'PASSPORT_NUMBER'}, {name:'DRIVER_LICENSE'}],
      items: [
        {
          text: 'Comptes',
          disabled: false,
          href: '/accounts',
        }
      ],
      fluid: true,
      enable: false,
      headers: [
          {
            text: '#',
            align: 'start',
            sortable: false,
            value: 'id',
          },
          { text: 'NOM', value: 'name' },
          { text: 'UID', value: 'uid', sortable: true },
          { text: 'TELEPHONE', value: 'phone' },
          { text: 'ADRESSE', value: 'address' },
          { text: 'SOUS-DOMAINE', value: 'prefix' },
          { text: 'CRÉÉ LE', value: 'created_at' },
          { text: 'ACTIONS', value: 'actions' },
        ],
        rows: [],
        roles: [],
        dialog:'',
        account: {
          name: '',
          rate_id:1, 
          prefix:'',
          address: '', 
          phone: '', 
          email: '', 
          contact_full_name: '', 
          contact_phone: '', 
          contact_email: '',
          contact_id:'',
          contact_id_type:"CIN"
        }
    }),
  components: { PreloaderComponent },
}
</script>

<style lang="scss" scoped>
.accounts-view {
  min-height: 100%;
}

.uid-prefix {
  font-family: ui-monospace, 'Cascadia Code', 'Consolas', monospace;
  font-size: 0.8125rem;
  letter-spacing: 0.02em;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  
  .page-title {
    font-size: 24px;
    font-weight: 600;
    margin: 0;
  }
  
  .page-subtitle {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    margin: 4px 0 0;
  }
}

.theme--dark .page-subtitle {
  color: rgba(255, 255, 255, 0.6);
}

.add-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 24px !important;
}

.content-container {
  padding: 0 24px 24px;
}

.data-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.card-header {
  padding: 16px 20px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 16px;
}

.theme--dark .card-header {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.table-search {
  max-width: 300px;
  
  ::v-deep .v-input__slot {
    min-height: 40px !important;
    border-radius: 8px !important;
  }
}

.results-chip {
  font-weight: 500;
}

.modern-table {
  ::v-deep th {
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
  }
  
  ::v-deep td {
    padding: 16px !important;
  }
}

.id-badge {
  font-weight: 600;
  color: #003F74;
  background: rgba(0, 63, 116, 0.08);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
}

.theme--dark .id-badge {
  color: #4A90D9;
  background: rgba(74, 144, 217, 0.15);
}

.account-name {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.prefix-link {
  display: inline-flex;
  align-items: center;
  color: #F68D3A !important;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
}

.action-btn {
  width: 32px !important;
  height: 32px !important;
  margin: 0 4px !important;
}

.action-btn-edit {
  background: rgba(0, 63, 116, 0.1) !important;
  color: #003F74 !important;
}

.action-btn-view {
  background: rgba(246, 141, 58, 0.1) !important;
  color: #F68D3A !important;
}

.section-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.5);
  margin: 8px 0;
}

.theme--dark .section-label {
  color: rgba(255, 255, 255, 0.5);
}

.dialog-title {
  font-size: 18px !important;
  font-weight: 600 !important;
  padding: 20px 24px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.dialog-content {
  padding: 24px !important;
  max-height: 60vh;
}

.dialog-actions {
  padding: 16px 24px !important;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.cancel-btn {
  text-transform: none !important;
}

.save-btn {
  text-transform: none !important;
  min-width: 120px !important;
}
</style>